import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Loader2, Save, Scale } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// Admin → Εργαλεία Συστήματος: Όριο υποθέσεων ανά δικαστή (τρέχον έτος ΓΑΚ, προδικαστικού + έδρας, όλοι οι τύποι/καταστήματα)
export const WorkloadLimitCard = () => {
  const [info, setInfo] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [judges, setJudges] = useState(null);
  const [loadingJudges, setLoadingJudges] = useState(false);

  const load = () => axios.get(`${API}/workload/limit`).then(r => { setInfo(r.data); setValue(String(r.data.limit)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const n = parseInt(value, 10);
    if (!(n >= 1)) { toast.error("Δώστε θετικό αριθμό"); return; }
    try {
      setSaving(true);
      const r = await axios.put(`${API}/workload/limit`, { limit: n });
      toast.success(r.data?.message || "Αποθηκεύτηκε");
      await load();
      if (judges) await loadJudges();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποθήκευσης ορίου");
    } finally { setSaving(false); }
  };

  const loadJudges = async () => {
    try {
      setLoadingJudges(true);
      const r = await axios.get(`${API}/workload/judges`);
      setJudges(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης φορτίου δικαστών");
    } finally { setLoadingJudges(false); }
  };

  const limit = info?.limit ?? 100;
  return (
    <div className="p-4 rounded-lg border bg-sky-50 border-sky-300" data-testid="workload-limit-card">
      <h3 className="font-medium text-sky-900 flex items-center gap-2"><Scale className="w-4 h-4" />Όριο υποθέσεων ανά δικαστή</h3>
      <p className="text-sm text-sky-800 mt-1">
        Κανένας δικαστής δεν μπορεί να χρεωθεί πάνω από <strong data-testid="workload-limit-current">{limit}</strong> υποθέσεις του
        τρέχοντος έτους ΓΑΚ ({info?.year || new Date().getFullYear()}) — ως Δικαστής Προδικαστικού/Εισηγητής <strong>ή</strong> Δικαστής Έδρας,
        ανεξάρτητα από τύπο δικαστηρίου και κατάστημα. Το όριο εφαρμόζεται σε κάθε ορισμό (Πινάκια, Προσδιορισμός, Καρτέλα, ΑΝΑΧΡΕΩΣΗ)
        και εμφανίζεται στα dropdown δικαστών ως «Χ/{limit}».
      </p>
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <div className="space-y-1">
          <Label className="text-xs text-sky-900">Νέο όριο</Label>
          <Input type="number" min="1" className="h-8 w-32 bg-white" value={value} onChange={e => setValue(e.target.value)} data-testid="workload-limit-input" />
        </div>
        <Button className="h-8 bg-sky-700 hover:bg-sky-800 text-white" onClick={save} disabled={saving || !info || String(info.limit) === String(value)} data-testid="workload-limit-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Αποθήκευση
        </Button>
        <Button variant="outline" className="h-8 border-sky-400 text-sky-800 hover:bg-sky-100" onClick={loadJudges} disabled={loadingJudges} data-testid="workload-judges-btn">
          {loadingJudges ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Φορτίο δικαστών
        </Button>
        {info && info.limit !== info.default && <Badge variant="outline" className="border-sky-400 text-sky-800">προεπιλογή: {info.default}</Badge>}
      </div>
      {judges && (
        <div className="mt-3 bg-white border border-sky-200 rounded max-h-72 overflow-y-auto" data-testid="workload-judges-table">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-100 sticky top-0"><tr>
              <th className="p-1.5 text-left border">Δικαστής</th><th className="p-1.5 text-left border">Βαθμός</th>
              <th className="p-1.5 text-left border">Τύπος</th><th className="p-1.5 text-center border">Υποθέσεις {judges.year}</th>
            </tr></thead>
            <tbody>
              {judges.judges.map((j, i) => {
                const full = j.case_count >= judges.limit;
                return (
                  <tr key={j.id || i} className={full ? "bg-red-50" : j.case_count >= judges.limit * 0.9 ? "bg-amber-50" : ""} data-testid={`workload-row-${j.id || i}`}>
                    <td className="p-1.5 border font-medium">{j.full_name}</td>
                    <td className="p-1.5 border text-slate-600">{j.grade || "—"}</td>
                    <td className="p-1.5 border text-slate-600">{j.court_type || "—"}</td>
                    <td className={`p-1.5 border text-center font-mono ${full ? "text-red-700 font-semibold" : ""}`}>{j.case_count}/{judges.limit}</td>
                  </tr>
                );
              })}
              {!judges.judges.length && <tr><td colSpan={4} className="p-2 text-center text-slate-500 italic">Δεν υπάρχουν δικαστές προδικαστικού</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkloadLimitCard;
